import {Slider, Tooltip, Typography} from '@mui/material'
import {Box, Loading} from '@viptech/react-components'
import {useEffect, useMemo, useRef, useState} from 'react'
import {Dim} from 'src/common/types/generics/ImageDetections'
import {resolveDetectionToPixels} from 'src/common/utils/demarcatedAreaCoordinates'
import useSnapshot from 'src/hooks/api/camera/useSnapshot'

type Props = {
  isLoading?: boolean
  data: HeatmapDetectionsType | undefined
  title: string
  isRefetching?: boolean
}

export type HeatmapDetectionsType = {
  cameraId: number
  width: number
  height: number
  objectName: string
  detections: Array<Array<number>>
}

type Point = {x: number; y: number; value: number}

const CANVAS_MAX_WIDTH = 750
const GRADIENT_STRENGTH = 2
const MIN_RADIUS = 20
const HEATMAP_LAYER_OPACITY_DEFAULT = 0.45
const HEATMAP_LAYER_OPACITY_MIN = 0.1
const HEATMAP_LAYER_OPACITY_MAX = 1
const HEATMAP_ALPHA_MIN = 0.15
const HEATMAP_ALPHA_MAX = 0.55

function getCanvasSize(reference: Dim) {
  if (reference.width <= 0 || reference.height <= 0) {
    return {width: CANVAS_MAX_WIDTH, height: Math.round(CANVAS_MAX_WIDTH * (9 / 16))}
  }

  const aspectRatio = reference.height / reference.width
  return {
    width: CANVAS_MAX_WIDTH,
    height: Math.max(1, Math.round(CANVAS_MAX_WIDTH * aspectRatio)),
  }
}

function HeatMapCard({isLoading, isRefetching, data, title}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const matrixRef = useRef<Float32Array[] | null>(null)
  const pointsRef = useRef<Point[]>([])
  const referenceSizeRef = useRef<Dim>({width: 0, height: 0})
  const renderScaleRef = useRef({x: 1, y: 1})
  const snapshotImageRef = useRef<HTMLImageElement | null>(null)
  const heatmapLayerRef = useRef<HTMLCanvasElement | null>(null)
  const drawFrameRef = useRef<number | null>(null)
  const layerOpacityRef = useRef(HEATMAP_LAYER_OPACITY_DEFAULT)

  const [loading, setLoading] = useState(false)
  const [layerOpacity, setLayerOpacity] = useState(HEATMAP_LAYER_OPACITY_DEFAULT)

  const {
    data: snapshot,
    isError,
    isLoading: isSnapshotLoading,
    isFetching: isSnapshotFetching,
  } = useSnapshot({
    cameraId: data?.cameraId ?? -1,
    enabled: !!data?.cameraId,
    retry: 0,
  })

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(v, max))

  const hasSnapshot = Boolean(snapshot?.time?.base64) && !isError

  const snapshotSrc = useMemo(() => {
    if (!hasSnapshot) return null
    return `data:image/jpeg;base64,${snapshot!.time.base64}`
  }, [hasSnapshot, snapshot])

  const kernelCache = useRef<Map<number, Float32Array>>(new Map())

  function getKernel(radius: number) {
    if (kernelCache.current.has(radius)) {
      return kernelCache.current.get(radius)!
    }

    const size = radius * 2 + 1
    const kernel = new Float32Array(size * size)

    let i = 0
    for (let y = -radius; y <= radius; y++) {
      for (let x = -radius; x <= radius; x++) {
        const dist = Math.sqrt(x * x + y * y)
        kernel[i++] = dist <= radius ? Math.pow(1 - dist / radius, GRADIENT_STRENGTH) : 0
      }
    }

    kernelCache.current.set(radius, kernel)
    return kernel
  }

  const rangeRef = useRef<{min: number; max: number}>({min: 0, max: 1})

  function buildHeatmapMatrix(reference: Dim, detections: Array<Array<number>>) {
    const matrix = Array.from({length: reference.height}, () => new Float32Array(reference.width))

    for (const box of detections) {
      const [x, y, width, height] = box
      const pixels = resolveDetectionToPixels({x, y, width, height}, reference)

      const cx = clamp(Math.floor(pixels.x + pixels.width / 2), 0, reference.width - 1)
      const cy = clamp(Math.floor(pixels.y + pixels.height / 2), 0, reference.height - 1)
      const radius = Math.max(MIN_RADIUS, Math.floor(Math.max(pixels.width, pixels.height) / 2))
      const kernel = getKernel(radius)

      let k = 0
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const value = kernel[k++]
          if (!value) continue

          const px = cx + dx
          const py = cy + dy

          if (px >= 0 && px < reference.width && py >= 0 && py < reference.height) {
            matrix[py][px] += value
          }
        }
      }
    }

    let min = Infinity
    let max = -Infinity
    const points: Point[] = []

    for (let y = 0; y < matrix.length; y++) {
      for (let x = 0; x < matrix[0].length; x++) {
        const v = matrix[y][x]
        if (v > 0) {
          points.push({x, y, value: v})
          min = Math.min(min, v)
          max = Math.max(max, v)
        }
      }
    }

    return {matrix, points, min, max}
  }

  function buildHeatmapLayerCanvas(canvasSize: Dim) {
    if (!heatmapLayerRef.current) {
      heatmapLayerRef.current = document.createElement('canvas')
    }

    const layer = heatmapLayerRef.current
    layer.width = canvasSize.width
    layer.height = canvasSize.height

    const ctx = layer.getContext('2d')
    if (!ctx) return

    const {min, max} = rangeRef.current
    const {x: scaleX, y: scaleY} = renderScaleRef.current

    ctx.clearRect(0, 0, layer.width, layer.height)

    for (const {x, y, value} of pointsRef.current) {
      const n = (value - min) / (max - min || 1)
      const hue = 240 - n * 240
      const alpha = HEATMAP_ALPHA_MIN + n * (HEATMAP_ALPHA_MAX - HEATMAP_ALPHA_MIN)

      ctx.fillStyle = `hsla(${hue}, 100%, 50%, ${alpha})`
      ctx.fillRect(x * scaleX, y * scaleY, Math.max(1, scaleX), Math.max(1, scaleY))
    }
  }

  function drawHeatmapOverlay(layerOpacity: number) {
    const canvas = canvasRef.current
    const heatmapLayer = heatmapLayerRef.current
    if (!canvas || !snapshotImageRef.current || !heatmapLayer) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(snapshotImageRef.current, 0, 0, canvas.width, canvas.height)

    ctx.save()
    ctx.globalAlpha = layerOpacity
    ctx.drawImage(heatmapLayer, 0, 0, canvas.width, canvas.height)
    ctx.restore()
  }

  function scheduleHeatmapRedraw(layerOpacity: number) {
    layerOpacityRef.current = layerOpacity

    if (drawFrameRef.current !== null) return

    drawFrameRef.current = requestAnimationFrame(() => {
      drawFrameRef.current = null
      drawHeatmapOverlay(layerOpacityRef.current)
    })
  }

  function handleLayerOpacityChange(value: number) {
    setLayerOpacity(value)
    scheduleHeatmapRedraw(value)
  }

  function drawCanvas(bgSrc: string, layerOpacity: number) {
    const canvas = canvasRef.current
    const matrix = matrixRef.current
    const reference = referenceSizeRef.current
    if (!canvas || !matrix || reference.width <= 0 || reference.height <= 0) return

    const bg = new Image()
    bg.src = bgSrc

    bg.onload = () => {
      snapshotImageRef.current = bg
      drawHeatmapOverlay(layerOpacity)
    }
  }

  useEffect(() => {
    if (!data || !canvasRef.current || !hasSnapshot || !snapshotSrc) return

    setLoading(true)

    const reference = {width: data.width, height: data.height}
    const canvasSize = getCanvasSize(reference)
    const canvas = canvasRef.current

    canvas.width = canvasSize.width
    canvas.height = canvasSize.height

    referenceSizeRef.current = reference
    renderScaleRef.current = {
      x: canvasSize.width / reference.width,
      y: canvasSize.height / reference.height,
    }

    const {matrix, points, min, max} = buildHeatmapMatrix(reference, data.detections)

    matrixRef.current = matrix
    pointsRef.current = points
    rangeRef.current = {min, max}

    buildHeatmapLayerCanvas(canvasSize)
    layerOpacityRef.current = layerOpacity
    drawCanvas(snapshotSrc, layerOpacity)
    setLoading(false)
  }, [data, hasSnapshot, snapshotSrc])

  useEffect(() => {
    return () => {
      if (drawFrameRef.current !== null) {
        cancelAnimationFrame(drawFrameRef.current)
      }
    }
  }, [])

  const showHeatmap = Boolean(data && hasSnapshot && data.detections.length > 0)

  useEffect(() => {
    if (!showHeatmap) return

    const canvas = canvasRef.current
    const tooltip = tooltipRef.current
    if (!canvas || !tooltip) return

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return

      const canvasX = (e.clientX - rect.left) * (canvas.width / rect.width)
      const canvasY = (e.clientY - rect.top) * (canvas.height / rect.height)
      const matrixX = Math.floor(canvasX / renderScaleRef.current.x)
      const matrixY = Math.floor(canvasY / renderScaleRef.current.y)

      const found = pointsRef.current.find((p) => Math.abs(p.x - matrixX) <= 2 && Math.abs(p.y - matrixY) <= 2)

      if (found) {
        tooltip.textContent = `Intensidade: ${found.value.toFixed(2)}`
        tooltip.style.left = `${e.clientX + 10}px`
        tooltip.style.top = `${e.clientY - 10}px`
        tooltip.style.visibility = 'visible'
      } else {
        tooltip.style.visibility = 'hidden'
      }
    }

    canvas.addEventListener('mousemove', onMove)
    canvas.addEventListener('mouseleave', () => (tooltip.style.visibility = 'hidden'))

    return () => {
      canvas.removeEventListener('mousemove', onMove)
    }
  }, [showHeatmap])

  if (!data) return null

  const canvasSize = getCanvasSize({width: data.width, height: data.height})
  const isSnapshotPending = isSnapshotLoading || isSnapshotFetching

  return (
    <Box backgroundColor='#F5F5F5' borderRadius='16px' position='relative' width='100%'>
      <Box display='flex' direction='column' padding='42px 0' rowGap='10px' align='center'>
        <Typography fontWeight={700} fontSize='16px' color='#0F4C81'>
          {title}
        </Typography>

        {(isLoading || loading || isRefetching || isSnapshotPending) && (
          <Tooltip title='Buscando atualizações'>
            <Box position='absolute' right='24px'>
              <Loading size='20px' />
            </Box>
          </Tooltip>
        )}

        {showHeatmap ? (
          <>
            <canvas ref={canvasRef} width={canvasSize.width} height={canvasSize.height} style={{maxWidth: '100%', height: 'auto'}} />

            <Box width='100%' maxWidth={`${canvasSize.width}px`} padding='0 24px' display='flex' direction='column' rowGap='4px'>
              <Box display='flex' align='center' justify='space-between'>
                <Typography fontSize='12px' color='#8E8E8E'>
                  Opacidade do mapa
                </Typography>
                <Typography fontSize='12px' color='#0F4C81' fontWeight={600}>
                  {Math.round(layerOpacity * 100)}%
                </Typography>
              </Box>

              <Slider
                value={layerOpacity}
                min={HEATMAP_LAYER_OPACITY_MIN}
                max={HEATMAP_LAYER_OPACITY_MAX}
                step={0.05}
                onChange={(_, value) => handleLayerOpacityChange(value as number)}
                sx={{color: '#0F4C81', padding: '8px 0'}}
              />

              <Box display='flex' align='center' justify='space-between'>
                <Typography fontSize='11px' color='#8E8E8E'>
                  Mais transparente
                </Typography>
                <Typography fontSize='11px' color='#8E8E8E'>
                  Mais visível
                </Typography>
              </Box>
            </Box>

            <div
              ref={tooltipRef}
              style={{
                position: 'fixed',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                visibility: 'hidden',
                pointerEvents: 'none',
                zIndex: 1000,
              }}
            />
          </>
        ) : (
          <Box width='100%' display='flex' align='center' justify='center' style={{minHeight: `${canvasSize.height}px`}}>
            <Typography color='#8E8E8E' fontSize='16px' textAlign='center'>
              {isSnapshotPending
                ? 'Carregando snapshot da câmera...'
                : !hasSnapshot
                  ? 'Snapshot indisponível para exibir o mapa de calor'
                  : 'Nenhum dado foi encontrado'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default HeatMapCard
